import React from 'react';
import ReactDOM from 'react-dom/client';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import App from './App';

// Define the theme colors
export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2', // Deep blue
      light: '#42a5f5', // Lighter blue
      dark: '#1565c0', // Darker blue
    },
    secondary: {
      main: '#2196f3', // Bright blue
      light: '#64b5f6', // Light blue
      dark: '#1976d2', // Deep blue
    },
    background: {
      default: '#f5f9ff', // Very light blue
      paper: '#ffffff',
    },
    text: {
      primary: '#1a237e', // Deep blue
      secondary: '#283593', // Medium blue
    },
    total: {
      main: '#1976d2', // Deep blue
      light: '#42a5f5', // Lighter blue
      dark: '#1565c0', // Darker blue
      contrastText: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 500,
      color: '#1a237e',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 500,
      color: '#1a237e',
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 500,
      color: '#1a237e',
    },
    body1: {
      fontSize: '1rem',
      color: '#283593',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          padding: '8px 16px',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
          border: '1px solid rgba(25, 118, 210, 0.1)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        },
      },
    },
  },
});

export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#90caf9', // Light blue
      light: '#e3f2fd', // Very light blue
      dark: '#42a5f5', // Medium blue
    },
    secondary: {
      main: '#64b5f6', // Bright blue
      light: '#bbdefb', // Light blue
      dark: '#42a5f5', // Medium blue
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
    text: {
      primary: '#e3f2fd',
      secondary: '#bbdefb',
    },
    total: {
      main: '#64b5f6', // Bright blue
      light: '#90caf9', // Light blue
      dark: '#42a5f5', // Medium blue
      contrastText: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 500,
      color: '#e3f2fd',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 500,
      color: '#e3f2fd',
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 500,
      color: '#e3f2fd',
    },
    body1: {
      fontSize: '1rem',
      color: '#bbdefb',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          padding: '8px 16px',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 6px rgba(0,0,0,0.2)',
          border: '1px solid rgba(144, 202, 249, 0.1)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        },
      },
    },
  },
});

// Add custom CSS for fonts
const customStyles = document.createElement('style');
customStyles.innerHTML = `
  @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@300;400;500;600;700&display=swap');
  body {
    background-color: #f8f9fa;
  }
`;
document.head.appendChild(customStyles);

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <ThemeProvider theme={lightTheme}>
      <CssBaseline />
      <App />
    </ThemeProvider>
  </React.StrictMode>
); 